import {
  type Board,
  type Coupon,
  type CouponId,
  type CouponIndex,
  type CouponStatus,
  couponValidator,
  getCouponId,
} from '../../../schema/coupon/entry.js';
import type { Definition } from '../../../schema/frameworks/entry.js';
import type { RSYSNum, USYSNum } from '../../../schema/system/entry.js';
import { typedObjectEntries } from '../../../schema/utils/entry.js';
import { BadRequestError } from '../error/BadRequestError.js';
import { calculatePrice } from './coupon/calculatePrice.js';
import { getCouponStatus } from './coupon/getCouponStatus.js';
import { selectSystemSizeFromOutcomes } from './coupon/selectSystemSizeFromOutcomes.js';
import { CouponBoardEntity, type ICouponBoardEntity } from './CouponBoardEntity.js';
import type { IDefinition } from './DefinitionEntity.js';

type PartialCoupon = ReturnType<typeof couponValidator.parse>;

type SystemNumber = RSYSNum | USYSNum;

export interface ICouponEntity {
  get couponId(): CouponId;
  get couponIndex(): CouponIndex;
  get model(): Coupon;
  get price(): number;
  get status(): CouponStatus;
  get systemNumber(): SystemNumber | undefined;
  get systemType(): Coupon['lastSelectedSystemType'];
  get product(): Coupon['product'];
  get sportkryssFactor(): Definition['sportkryssFactors'][number];
  get sportkryssNumber(): Coupon['sportkryssNumber'];
  get drawCount(): Definition['weeks'][number];
  get drawNumber(): Coupon['drawNumber'];
  get betAmount(): Coupon['betAmount'];

  getBoards(): Generator<ICouponBoardEntity, void, undefined>;
  getBoard(boardIndex: number): ICouponBoardEntity | undefined;

  validateBetAmount(betAmount: number): Coupon['betAmount'];
  validateDrawCount(drawCount: number): Coupon['week'];

  validate(payload: unknown): PartialCoupon;
}

export class CouponEntity implements ICouponEntity {
  private readonly _model: Coupon;
  private readonly _couponIndex: CouponIndex;
  private readonly _definition: IDefinition;

  constructor(model: Coupon, couponIndex: CouponIndex, definition: IDefinition) {
    // TODO: [CA] immer seems to be initialized but not on the first run, is it skipped during "hydration" from LS?
    this._model = model;
    this._couponIndex = couponIndex;
    this._definition = definition;
  }

  get couponId(): CouponId {
    const { product, drawNumber, couponType } = this._model;

    return getCouponId(product, drawNumber, couponType);
  }

  get couponIndex(): CouponIndex {
    return this._couponIndex;
  }

  get model(): Coupon {
    return this._model;
  }

  get price(): number {
    return CouponEntity.getPrice(this.model, { definition: this.definition.model });
  }

  get status(): CouponStatus {
    return CouponEntity.getStatus(this.model, { definition: this.definition.model });
  }

  get systemNumber(): SystemNumber | undefined {
    const { lastSelectedSystemType: systemType, rSystem, uSystem } = this.model;
    let system: SystemNumber | undefined = undefined;

    if (systemType === 'RSYS') {
      system = rSystem;
    } else if (systemType === 'USYS') {
      system = uSystem;
    }

    return system;
  }

  get systemType(): Coupon['lastSelectedSystemType'] {
    return this.model.lastSelectedSystemType;
  }

  get product(): Coupon['product'] {
    return this.model.product;
  }

  get sportkryssFactor(): Definition['sportkryssFactors'][number] {
    return this.definition.sportkryssFactors[this.model.sportkryss] ?? 0;
  }

  get sportkryssNumber(): Coupon['sportkryssNumber'] {
    return this.model.sportkryssNumber;
  }

  get drawCount(): Definition['weeks'][number] {
    return this.definition.drawsCount[this.model.week] ?? 1;
  }

  get drawNumber(): Coupon['drawNumber'] {
    return this.model.drawNumber;
  }

  get betAmount(): Coupon['betAmount'] {
    return this.model.betAmount;
  }

  protected get definition(): IDefinition {
    return this._definition;
  }

  static getPrice(coupon: Coupon, dependencies: { definition: Definition }): ReturnType<typeof calculatePrice> {
    return calculatePrice(coupon, dependencies);
  }

  static getSystemSizeFromOutcomes(outcomes: Board['outcomes']): ReturnType<typeof selectSystemSizeFromOutcomes> {
    return selectSystemSizeFromOutcomes(outcomes);
  }

  static getStatus(coupon: Coupon, dependencies: { definition: Definition }): ReturnType<typeof getCouponStatus> {
    return getCouponStatus(coupon, dependencies);
  }

  *getBoards(): Generator<ICouponBoardEntity, void, undefined> {
    for (let boardIndex = 0; boardIndex < this.model.boards.length; boardIndex++) {
      const boardEntity = this.getBoard(boardIndex);
      if (boardEntity) {
        yield boardEntity;
      }
    }
  }

  getBoard(boardIndex: number): ICouponBoardEntity | undefined {
    const boardModel = this.model.boards[boardIndex];

    if (!boardModel) {
      return undefined;
    }

    const couponBoardIdentifiers = {
      couponId: this.couponId,
      couponIndex: this.couponIndex,
      boardIndex,
    };

    return new CouponBoardEntity(boardModel, couponBoardIdentifiers, this._definition);
  }

  validateBetAmount(betAmount: number): Coupon['betAmount'] {
    if (betAmount < this.definition.minBetAmount || betAmount > this.definition.maxBetAmount) {
      throw new BadRequestError(
        `Invalid bet amount; min value: ${this.definition.minBetAmount}, max value: ${this.definition.maxBetAmount}`
      );
    }

    return betAmount;
  }

  validateDrawCount(drawCount: number): Coupon['week'] {
    const castedDrawCount = drawCount as Coupon['week'];
    if (!this.definition.drawsCount.includes(castedDrawCount)) {
      throw new BadRequestError(`Invalid draw count; ${this.definition.drawsCount.join(', ')}`);
    }

    return castedDrawCount;
  }

  validate(payload: unknown): PartialCoupon {
    const parsedEntity = couponValidator.parse(payload);
    const updateEntity: PartialCoupon = {};

    for (const [field, value] of typedObjectEntries(parsedEntity)) {
      if (!value) {
        // skip undefined values for now
        continue;
      }

      switch (field) {
        case 'betAmount': {
          updateEntity[field] = this.validateBetAmount(value);
          continue;
        }
        case 'week': {
          updateEntity[field] = this.validateDrawCount(value);
          continue;
        }
        // add more validators...
        default:
          continue;
      }
    }

    return updateEntity;
  }
}
